import {
  ButtonWithLoading,
  ErrorModal,
  MesfModal,
  getMomentTz,
} from "@dexteel/mesf-core";
import { Button, Grid, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { useEffect, useState } from "react";

import { UnLinkedHeat } from "../../../../caster/processes/models/UnLinkedHeat";
import {
  linkEAFProcess,
  searchUnlinkedHeats as searchEAFUnlinkedHeats,
} from "../../../../eaf/eaf-processes/repositories/EAFRepository";
import {
  linkLMFProcess,
  searchUnlinkedHeats as searchLMFUnlinkedHeats,
} from "../../../../lmf/lmf-processes/repositories/LMFRepository";
import { Process } from "../../../models/ProcessGanttChart";
import {
  linkProcess as linkCasterProcess,
  searchUnlinkedHeats as searchCasterUnlinkedHeats,
} from "../../../repositories/ProductionRepository";

const moment = getMomentTz();

type Props = {
  show: boolean;
  onHide: () => void;
  process: Process | null;
  onNotify: (message: string) => void;
};
export const LinkProcess = ({ show, onHide, process, onNotify }: Props) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [unlinkedHeats, setUnLinkedHeats] = useState<UnLinkedHeat[]>([]);
  const [selectedHeat, setSelectedHeat] = useState<any>(null);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");

  const onSubmit = async () => {
    if (selectedHeat === null) {
      setError("Please select a heat");
      return;
    }

    if (!process) return;
    //Use the linkProcess function based on the process code
    const linkProcess =
      process.ProcessCode === "E"
        ? linkEAFProcess
        : process.ProcessCode === "L"
          ? linkLMFProcess
          : linkCasterProcess;
    const response = await linkProcess(
      process.ProcessId,
      selectedHeat.HeatId,
      true,
    );
    if (response.ok) {
      onHide();
      onNotify("Process linked successfully");
    } else {
      setError(response.message);
    }
  };

  const handleSearchChange = async () => {
    if (!process) return;
    //Use the searchUnlinkedHeats function based on the process code
    const searchUnlinkedHeats =
      process.ProcessCode === "E"
        ? searchEAFUnlinkedHeats
        : process.ProcessCode === "L"
          ? searchLMFUnlinkedHeats
          : searchCasterUnlinkedHeats;
    const heats = await searchUnlinkedHeats(searchText, process.Start);
    if (heats.ok) {
      const rows = heats.data?.tables?.[0]?.rows ?? [];
      setUnLinkedHeats(rows);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<{}>, value: string) => {
    setSearchText(value);
  };

  const handleKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      await handleSearchChange();
    }
  };

  const handleChange = (
    event: React.ChangeEvent<{}>,
    newValue: UnLinkedHeat | null,
  ) => {
    setSelectedHeat(newValue as any);
  };

  useEffect(() => {
    if (show) {
      setIsSubmitLoading(false);
      (async () => {
        setSelectedHeat(null);
        if (process === undefined || process === null) return;
        setTitle(
          `Link ${process.ProcessCode === "E" ? "EAF" : process.ProcessCode === "L" ? "LMF" : "Caster"} Process | ${process.ProcessCode === "E" ? "Start" : process.ProcessCode === "L" ? "Arrive" : "Ladle Open"}: ${moment(process.Start).format("YYYY/MM/DD HH:mm")}`,
        );
        const searchUnlinkedHeats =
          process.ProcessCode === "E"
            ? searchEAFUnlinkedHeats
            : process.ProcessCode === "L"
              ? searchLMFUnlinkedHeats
              : searchCasterUnlinkedHeats;
        const heats = await searchUnlinkedHeats("", process.Start);
        if (heats.ok) {
          const rows = heats.data?.tables?.[0]?.rows ?? [];
          setUnLinkedHeats(rows);
        } else {
          setError(heats.message);
        }
      })();
    }
  }, [show]);
  return (
    <>
      <MesfModal
        maxWidth="xl"
        open={show}
        handleClose={() => {
          onHide();
        }}
        id="linkCasterProcess"
        title={title}
      >
        <MesfModal.Content
          style={{
            padding: "15px 30px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <Autocomplete
                options={unlinkedHeats}
                getOptionLabel={(option) => option.HeatName}
                value={selectedHeat}
                onChange={handleChange}
                inputValue={searchText}
                onInputChange={handleInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Heat"
                    placeholder="Search Heat"
                    variant="outlined"
                    fullWidth
                    onKeyDown={handleKeyDown}
                  />
                )}
              />
            </Grid>
          </Grid>
        </MesfModal.Content>

        <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => onHide()}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <ButtonWithLoading
                fullWidth
                isLoading={isSubmitLoading}
                disabled={isSubmitLoading}
                variant="contained"
                color="primary"
                onClick={onSubmit}
              >
                Save
              </ButtonWithLoading>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>

      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
